import { useState, useEffect, useCallback } from 'react'
import {
  Gift, Settings, Users, BarChart3, TrendingUp,
  Copy, X, Save, Loader2, RefreshCw, Search,
  CheckCircle, XCircle, AlertCircle, Plus,
  ChevronLeft, ChevronRight
} from 'lucide-react'
import { toast } from 'react-hot-toast'
import { referralService, ReferralConfig, ReferralCode, Referral, ReferralStats } from '../services/referralService'

type TabType = 'config' | 'analytics' | 'codes'

const ReferralPage = () => {
  const [activeTab, setActiveTab] = useState<TabType>('config')
  const [loading, setLoading] = useState(true)

  // Config state
  const [config, setConfig] = useState<ReferralConfig | null>(null)
  const [editConfig, setEditConfig] = useState<Partial<ReferralConfig> | null>(null)

  // Analytics state
  const [stats, setStats] = useState<ReferralStats | null>(null)
  const [topReferrers, setTopReferrers] = useState<any[]>([])
  const [conversionData, setConversionData] = useState<any>(null)
  const [referrals, setReferrals] = useState<Referral[]>([])
  const [referralsPage, setReferralsPage] = useState(1)
  const [referralsTotal, setReferralsTotal] = useState(0)

  // Codes state
  const [codes, setCodes] = useState<ReferralCode[]>([])
  const [codesTotal, setCodesTotal] = useState(0)
  const [codesPage, setCodesPage] = useState(1)
  const [bulkCount, setBulkCount] = useState(10)
  const [generatingBulk, setGeneratingBulk] = useState(false)

  const loadConfig = useCallback(async () => {
    try {
      const res = await referralService.getConfig()
      if (res.success) setConfig(res.data)
    } catch { /* ignore */ }
  }, [])

  const loadAnalytics = useCallback(async () => {
    try {
      const [statsRes, topRes, conversionRes, referralsRes] = await Promise.all([
        referralService.getStats(),
        referralService.getTopReferrers(),
        referralService.getConversionRate(),
        referralService.getAllReferrals(referralsPage),
      ])
      if (statsRes.success) setStats(statsRes.data)
      if (topRes.success) setTopReferrers(topRes.data)
      if (conversionRes.success) setConversionData(conversionRes.data)
      if (referralsRes.success) {
        setReferrals(referralsRes.data)
        setReferralsTotal(referralsRes.total)
      }
    } catch { /* ignore */ }
  }, [referralsPage])

  const loadCodes = useCallback(async () => {
    try {
      const res = await referralService.getAllCodes({ page: codesPage, limit: 20 })
      if (res.success) {
        setCodes(res.data)
        setCodesTotal(res.total)
      }
    } catch { /* ignore */ }
  }, [codesPage])

  useEffect(() => {
    setLoading(true)
    const loaders: Record<TabType, () => Promise<void>> = {
      config: loadConfig,
      analytics: loadAnalytics,
      codes: loadCodes,
    }
    loaders[activeTab]().finally(() => setLoading(false))
  }, [activeTab, loadConfig, loadAnalytics, loadCodes])

  // Config handlers
  const handleSaveConfig = async () => {
    if (!editConfig) return
    try {
      const res = await referralService.updateConfig(editConfig)
      if (res.success) {
        setConfig(res.data)
        setEditConfig(null)
        toast.success('Configuration mise à jour')
      }
    } catch {
      toast.error('Erreur lors de la mise à jour')
    }
  }

  // Code handlers
  const handleDisableCode = async (codeId: string) => {
    if (!confirm('Désactiver ce code de parrainage ?')) return
    try {
      const res = await referralService.disableCode(codeId)
      if (res.success) {
        toast.success('Code désactivé')
        loadCodes()
      }
    } catch {
      toast.error('Erreur lors de la désactivation')
    }
  }

  const handleGenerateBulk = async () => {
    setGeneratingBulk(true)
    try {
      const res = await referralService.generateBulkCodes(bulkCount)
      if (res.success) {
        toast.success(`${res.data.length} codes générés avec succès`)
        loadCodes()
      }
    } catch {
      toast.error('Erreur lors de la génération')
    } finally {
      setGeneratingBulk(false)
    }
  }

  const tabs: { id: TabType; label: string; icon: any }[] = [
    { id: 'config', label: 'Configuration', icon: Settings },
    { id: 'analytics', label: 'Analytiques', icon: BarChart3 },
    { id: 'codes', label: 'Codes', icon: Copy },
  ]

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Gestion du parrainage</h1>
        <p className="mt-2 text-gray-600">Configurez et gérez le programme de parrainage</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-8">
        <div className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-1 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto" />
        </div>
      ) : (
        <>
          {/* Config Tab */}
          {activeTab === 'config' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Configuration du programme</h2>

              {editConfig ? (
                <div className="space-y-4 max-w-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Montant de la récompense (points)</label>
                    <input
                      type="number"
                      value={editConfig.reward_amount || 0}
                      onChange={(e) => setEditConfig({ ...editConfig, reward_amount: Number(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Montant minimum de commande</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editConfig.minimum_order_amount || 0}
                      onChange={(e) => setEditConfig({ ...editConfig, minimum_order_amount: Number(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Expiration du code (jours)</label>
                    <input
                      type="number"
                      value={editConfig.referral_code_expiry_days || 0}
                      onChange={(e) => setEditConfig({ ...editConfig, referral_code_expiry_days: Number(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max parrainages par utilisateur</label>
                    <input
                      type="number"
                      value={editConfig.max_referrals_per_user || 0}
                      onChange={(e) => setEditConfig({ ...editConfig, max_referrals_per_user: Number(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={editConfig.is_active ?? config?.is_active ?? true}
                        onChange={(e) => setEditConfig({ ...editConfig, is_active: e.target.checked })}
                        className="rounded border-gray-300 text-primary-600"
                      />
                      <span className="text-sm text-gray-700">Programme actif</span>
                    </label>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={handleSaveConfig}
                      className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Enregistrer
                    </button>
                    <button
                      onClick={() => setEditConfig(null)}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Annuler
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500">Récompense</p>
                      <p className="text-2xl font-bold text-gray-900">{config?.reward_amount || 0} pts</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500">Minimum commande</p>
                      <p className="text-2xl font-bold text-gray-900">{config?.minimum_order_amount || 0} €</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500">Expiration</p>
                      <p className="text-2xl font-bold text-gray-900">{config?.referral_code_expiry_days || 0} jours</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500">Max parrainages</p>
                      <p className="text-2xl font-bold text-gray-900">{config?.max_referrals_per_user || 0}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${config?.is_active ? 'bg-green-500' : 'bg-red-500'}`} />
                    <span className="text-sm text-gray-600">{config?.is_active ? 'Programme actif' : 'Programme inactif'}</span>
                  </div>
                  <button
                    onClick={() => setEditConfig(config ? { ...config } : {
                      reward_amount: 0, minimum_order_amount: 0,
                      referral_code_expiry_days: 30, max_referrals_per_user: 10, is_active: true,
                    })}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Modifier
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div>
              {/* Stats Cards */}
              {stats && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Users className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Total parrainages</p>
                        <p className="text-2xl font-bold text-gray-900">{stats.total_referrals}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Réussis</p>
                        <p className="text-2xl font-bold text-gray-900">{stats.successful_referrals}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-yellow-100 rounded-lg">
                        <TrendingUp className="w-5 h-5 text-yellow-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Conversion</p>
                        <p className="text-2xl font-bold text-gray-900">{(stats.conversion_rate * 100).toFixed(1)}%</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <Gift className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Récompenses</p>
                        <p className="text-2xl font-bold text-gray-900">{stats.rewards_earned}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Conversion Data */}
              {conversionData && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
                  <h3 className="font-semibold text-gray-900 mb-4">Taux de conversion</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-gray-900">{conversionData.total_invited}</p>
                      <p className="text-sm text-gray-500">Invités</p>
                    </div>
                    <div className="text-center">
                      <p className="text-3xl font-bold text-gray-900">{conversionData.total_converted}</p>
                      <p className="text-sm text-gray-500">Convertis</p>
                    </div>
                    <div className="text-center">
                      <p className="text-3xl font-bold text-primary-600">{(conversionData.rate * 100).toFixed(1)}%</p>
                      <p className="text-sm text-gray-500">Taux de conversion</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Top Referrers */}
              {topReferrers.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
                  <h3 className="font-semibold text-gray-900 mb-4">Top parrains</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Parrainages</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Récompenses</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {topReferrers.map((referrer, index) => (
                          <tr key={referrer.user_id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-500">{index + 1}</td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{referrer.name || 'N/D'}</td>
                            <td className="px-4 py-3 text-sm text-gray-500">{referrer.email}</td>
                            <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">{referrer.total_referrals}</td>
                            <td className="px-4 py-3 text-sm text-right text-green-600">+{referrer.rewards_earned}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Recent Referrals */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Parrainages récents</h3>
                {referrals.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">Aucun parrainage</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Parrain</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Filleul</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Récompense</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {referrals.map((ref) => (
                          <tr key={ref.referral_id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                              {new Date(ref.created_at).toLocaleDateString('fr-FR')}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">{ref.referrer_email || ref.referrer_id.slice(0, 8)}</td>
                            <td className="px-4 py-3 text-sm text-gray-500">{ref.referred_email || 'N/D'}</td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                ref.status === 'completed'
                                  ? 'bg-green-100 text-green-800'
                                  : ref.status === 'pending'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {ref.status === 'completed' ? 'Terminé' : ref.status === 'pending' ? 'En attente' : 'Expiré'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-green-600 font-medium">+{ref.reward_amount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {referralsTotal > 20 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-500">Total: {referralsTotal}</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setReferralsPage(p => Math.max(1, p - 1))}
                        disabled={referralsPage <= 1}
                        className="p-2 rounded border border-gray-300 disabled:opacity-50"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setReferralsPage(p => p + 1)}
                        disabled={referralsPage * 20 >= referralsTotal}
                        className="p-2 rounded border border-gray-300 disabled:opacity-50"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-center mt-6">
                <button
                  onClick={loadAnalytics}
                  className="inline-flex items-center px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Actualiser
                </button>
              </div>
            </div>
          )}

          {/* Codes Tab */}
          {activeTab === 'codes' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Codes de parrainage</h2>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={bulkCount}
                      onChange={(e) => setBulkCount(Number(e.target.value))}
                      min={1}
                      max={100}
                      className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <button
                      onClick={handleGenerateBulk}
                      disabled={generatingBulk}
                      className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                    >
                      {generatingBulk ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Plus className="w-4 h-4 mr-2" />
                      )}
                      Générer en masse
                    </button>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Utilisateur</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actif</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Parrainages</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Récompenses</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {codes.map((code) => (
                        <tr key={code.referral_code_id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <span className="font-mono font-bold text-sm text-gray-900">{code.code}</span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {code.user_email || code.user_name || code.user_id.slice(0, 8)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {code.is_active ? (
                              <CheckCircle className="w-4 h-4 text-green-500 inline" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-500 inline" />
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900">{code.total_referrals}</td>
                          <td className="px-4 py-3 text-sm text-right text-green-600">+{code.rewards_earned}</td>
                          <td className="px-4 py-3 text-right">
                            {code.is_active && (
                              <button
                                onClick={() => handleDisableCode(code.referral_code_id)}
                                className="text-sm text-red-600 hover:text-red-800"
                              >
                                Désactiver
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {codes.length === 0 && (
                  <p className="text-center py-8 text-gray-500">Aucun code de parrainage</p>
                )}
              </div>

              {codesTotal > 20 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-gray-500">Total: {codesTotal}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCodesPage(p => Math.max(1, p - 1))}
                      disabled={codesPage <= 1}
                      className="p-2 rounded border border-gray-300 disabled:opacity-50"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setCodesPage(p => p + 1)}
                      disabled={codesPage * 20 >= codesTotal}
                      className="p-2 rounded border border-gray-300 disabled:opacity-50"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default ReferralPage
